import { track } from '../analytics.js';
import { safeSet } from '../lib/storage.js';
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const AuthContext = createContext(null);

const API = import.meta.env.VITE_API_URL || '/api';

// ── Dev-заглушка авторизации (ТОЛЬКО для `npm run dev`) ─────────────────────
// Чтобы при локальной разработке не проходить логин каждый раз: в dev-режиме
// (import.meta.env.DEV) подставляется фиктивный пользователь и токен, а экран
// входа пропускается. В production-сборке заглушка НИКОГДА не активируется.
// Реальный сохранённый вход (после настоящего логина) всегда имеет приоритет.
//
// Хочешь увидеть/протестировать экран входа в dev? В консоли браузера:
//     localStorage.dev_force_login = '1'   → обновить страницу
// Вернуть заглушку:  delete localStorage.dev_force_login → обновить страницу.
const DEV_AUTH = !!import.meta.env.DEV &&
  !(typeof localStorage !== 'undefined' && localStorage.getItem('dev_force_login'));
const DEV_TOKEN = 'dev-stub-token';
const DEV_USER = {
  id: '00000000-0000-0000-0000-000000000000',
  username: 'dev', firstName: 'Dev', lastName: '',
  email: 'dev@local', avatarUrl: null, avatar_url: null, publicTabs: [],
};

const LOCAL_KEYS = [
  'studied_v1','custom_terms_v1','smart_tasks_v1',
  'cal_history_v1','prep_ideas_v1'
];

export function AuthProvider({ children }) {
  const [user, setUser]   = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('auth_user')); if (s) return s; } catch {}
    return DEV_AUTH ? DEV_USER : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('auth_token') || (DEV_AUTH ? DEV_TOKEN : null));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('auth_refresh') || null);

  // Refs hold the latest tokens so apiFetch/refresh read current values without
  // depending on React's async render timing.
  const tokenRef   = useRef(token);
  const refreshRef = useRef(refreshToken);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { refreshRef.current = refreshToken; }, [refreshToken]);

  // Keep tabs in sync: when another tab rotates the refresh token, logs in, or logs
  // out, mirror it here so a stale tab never replays an already-rotated token (which
  // reuse-detection would treat as theft and log everyone out).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'auth_token') { tokenRef.current = e.newValue; setToken(e.newValue); }
      else if (e.key === 'auth_refresh') { refreshRef.current = e.newValue; setRefreshToken(e.newValue); }
      else if (e.key === 'auth_user') { try { setUser(e.newValue ? JSON.parse(e.newValue) : null); } catch {} }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const applyToken = (t) => { tokenRef.current = t; setToken(t); if (t) safeSet('auth_token', t); };

  // Серверные свойства аккаунта → кэш браузера (самолечение после чисток/нового девайса).
  const seedFromUser = (u) => {
    try {
      if (u?.onboarding_done) safeSet('onb_done_v1', '1');
      if (u?.hr_max) { safeSet('hr_max_v1', JSON.stringify(u.hr_max)); safeSet('hr_prompted', '1'); }
      const prefs = u?.ui_prefs || {};
      if (prefs.placement && typeof prefs.placement === 'object') {
        safeSet('nav_placement_v1', JSON.stringify(prefs.placement));
        try { window.dispatchEvent(new Event('placement_changed')); } catch {}
      }
      if (prefs.todayLayout && Array.isArray(prefs.todayLayout.order)) {
        try { localStorage.setItem('today_layout_v1', JSON.stringify(prefs.todayLayout)); } catch {}
      }
      if (prefs.theme) safeSet('ui_theme', prefs.theme);
      if (prefs.lang) {
        safeSet('ui_lang', prefs.lang);
        try { window.dispatchEvent(new Event('lang_changed')); } catch {}
      }
    } catch {}
  };

  const login = (u, t, rt) => {
    seedFromUser(u);
    // Clear another user's cached local data if a different account logs in.
    try {
      const prev = JSON.parse(localStorage.getItem('auth_user'));
      if (prev && prev.id !== u.id) LOCAL_KEYS.forEach(k => localStorage.removeItem(k));
    } catch {}
    // Refresh token now lives in an httpOnly cookie set by the server; we only
    // remember THAT a session can refresh. 'cookie' is a sentinel — never a real
    // token — so nothing steal-worthy sits in localStorage anymore.
    const marker = rt ? 'cookie' : null;
    tokenRef.current = t; refreshRef.current = marker;
    setUser(u); setToken(t); setRefreshToken(marker);
    safeSet('auth_user', JSON.stringify(u));
    safeSet('auth_token', t);
    if (marker) safeSet('auth_refresh', marker); else localStorage.removeItem('auth_refresh');
    track('login', { uid: u && u.id });
  };

  const logout = useCallback(() => {
    const rt = refreshRef.current;
    if (rt) {
      // Best-effort server-side revoke so the refresh token can't mint more access tokens.
      fetch(`${API}/auth/logout`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rt && rt !== 'cookie' ? { refreshToken: rt } : {}),
      }).catch(() => {});
    }
    tokenRef.current = null; refreshRef.current = null;
    setUser(null); setToken(null); setRefreshToken(null);
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh');
  }, []);

  const updateUser = (updated) => {
    setUser(prev => {
      const merged = { ...prev, ...updated };
      safeSet('auth_user', JSON.stringify(merged));
      seedFromUser(merged);
      return merged;
    });
  };

  // Single-flight refresh: many concurrent 401s trigger only one /auth/refresh call.
  // Returns { token, denied }: `denied` is true ONLY when the server explicitly
  // rejected the refresh token (401/403). Network errors / 5xx (deploy restarts,
  // waking Wi-Fi, flaky mobile) are NOT denial — the session must survive them.
  const refreshing = useRef(null);
  // exp из JWT (мс) — чтобы self-heal не ротировал токены без нужды.
  const jwtExpMs = (t) => {
    try { return (JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp || 0) * 1000; }
    catch { return 0; }
  };
  const LOCK_KEY = 'auth_rt_lock';
  const lockIdRef = useRef('L' + Math.random().toString(36).slice(2, 9));
  const doRefresh = useCallback(() => {
    if (refreshing.current) return refreshing.current;
    const rt = refreshRef.current;
    if (!rt) return Promise.resolve({ token: null, denied: false });

    // Cross-tab lock: with rotation, two tabs refreshing the same token would
    // make the second look like token theft (reuse) and kill the session.
    // If another tab holds a fresh lock, wait for it to finish and adopt the
    // token it writes to localStorage (the storage listener updates our refs).
    let foreign = null;
    try { foreign = JSON.parse(localStorage.getItem(LOCK_KEY) || 'null'); } catch {}
    if (foreign && foreign.id !== lockIdRef.current && Date.now() - (foreign.t || 0) < 8000) {
      refreshing.current = (async () => {
        const startTok = tokenRef.current;
        for (let i = 0; i < 32; i++) {           // ждём до ~8с
          await new Promise(r => setTimeout(r, 250));
          if (tokenRef.current && tokenRef.current !== startTok) return { token: tokenRef.current, denied: false };
          let l = null; try { l = JSON.parse(localStorage.getItem(LOCK_KEY) || 'null'); } catch {}
          if (!l) break;                          // чужой рефреш завершился
        }
        return { token: tokenRef.current !== startTok ? tokenRef.current : null, denied: false };
      })().finally(() => { refreshing.current = null; });
      return refreshing.current;
    }

    try { safeSet(LOCK_KEY, JSON.stringify({ id: lockIdRef.current, t: Date.now() })); } catch {}
    const releaseLock = () => {
      try {
        const l = JSON.parse(localStorage.getItem(LOCK_KEY) || 'null');
        if (l && l.id === lockIdRef.current) localStorage.removeItem(LOCK_KEY);
      } catch {}
    };

    // Cookie mode: the httpOnly cookie rides automatically (same-origin fetch).
    // A real token in `rt` means a legacy session from before the migration —
    // send it once in the body; the server answers with a cookie and we switch.
    const legacyRt = rt !== 'cookie' ? rt : null;
    refreshing.current = fetch(`${API}/auth/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(legacyRt ? { refreshToken: legacyRt } : {}),
    })
      .then(async (r) => {
        if (r.ok) {
          const d = await r.json().catch(() => null);
          const nt = d && d.token ? d.token : null;
          if (nt) {
            applyToken(nt);
            // Success means the server just set/renewed the httpOnly cookie —
            // record the sentinel (this also migrates legacy sessions: the real
            // token that used to live here is overwritten and never stored again).
            refreshRef.current = 'cookie';
            setRefreshToken('cookie');
            safeSet('auth_refresh', 'cookie');
            return { token: nt, denied: false };
          }
          return { token: null, denied: true };   // 200 без токена = сервер не признал
        }
        // 401/403 — сервер явно отверг токен; всё остальное (5xx, 429…) — временный сбой.
        return { token: null, denied: r.status === 401 || r.status === 403 };
      })
      .catch(() => ({ token: null, denied: false }))  // сеть недоступна — НЕ разлогиниваем
      .finally(() => { releaseLock(); refreshing.current = null; });
    return refreshing.current;
  }, []);

  // Authorized fetch. On 401 it transparently refreshes the access token once and
  // retries the request; it only logs out when the SERVER rejects the refresh
  // token. Transient failures (offline, 5xx during deploys) keep the session.
  const apiFetch = useCallback(async (path, options = {}, _retry = true) => {
    const doFetch = (tok) => fetch(`${API}${path}`, {
      ...options,
      headers: {
        // FormData обязан идти БЕЗ ручного Content-Type: браузер сам ставит
        // multipart/form-data с boundary. Жёсткий json ломал загрузку файлов —
        // сервер не мог распарсить тело, и «Загрузка…» висела вечно.
        ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        ...options.headers,
      },
    });
    let res = await doFetch(tokenRef.current);
    // В dev-заглушке (фиктивный токен) не разлогиниваем на 401 — просто отдаём
    // ответ, чтобы остаться в приложении даже без поднятого бэкенда.
    const stub = tokenRef.current === DEV_TOKEN;
    if (res.status === 401 && _retry && refreshRef.current) {
      const r = await doRefresh();
      if (r && r.token) {
        res = await doFetch(r.token);
        if (res.status === 401 && !stub) { logout(); return null; } // свежий токен отвергнут — сессия мертва
        return res;
      }
      if (r && r.denied && !stub) { logout(); return null; }        // сервер явно отверг refresh-токен
      return res; // временный сбой (сеть/5xx): сессию сохраняем, отдаём 401 вызывающему
    }
    if (res.status === 401 && !stub && !refreshRef.current) { logout(); return null; } // восстановить нечем
    return res;
  }, [doRefresh, logout]);

  // On startup (and when the token changes), pull the latest profile so fields
  // edited on another device show up. Goes through apiFetch so an expired access
  // token refreshes transparently instead of silently failing.
  useEffect(() => {
    if (!tokenRef.current || tokenRef.current === DEV_TOKEN) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/users/me');
        if (!res || !res.ok || cancelled) return;
        const me = await res.json();
        if (cancelled || !me) return;
        const fresh = {
          id: me.id, username: me.username, email: me.email,
          firstName: me.first_name, lastName: me.last_name,
          avatarUrl: me.avatar_url || null, avatar_url: me.avatar_url || null,
          publicTabs: me.public_tabs || [],
        };
        setUser(prev => {
          const merged = { ...prev, ...fresh };
          safeSet('auth_user', JSON.stringify(merged));
          return merged;
        });
      } catch {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // If a refresh failed transiently (offline cold start, deploy hiccup), heal
  // the session as soon as connectivity or focus returns. Single-flight +
  // cross-tab lock make these calls safe and cheap.
  useEffect(() => {
    const heal = () => {
      if (!refreshRef.current || tokenRef.current === DEV_TOKEN) return;
      // Rotate only when the access token is actually near death (<10 min) or
      // unreadable. Healing on every tab-return used to rotate the refresh token
      // dozens of times a day — each rotation is a chance for a lost response,
      // which is exactly the race that ends in refresh_reused and a logout.
      const exp = tokenRef.current ? jwtExpMs(tokenRef.current) : 0;
      if (exp && exp - Date.now() > 10 * 60 * 1000) return;
      doRefresh();
    };
    window.addEventListener('online', heal);
    const onVis = () => { if (!document.hidden) heal(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('online', heal); document.removeEventListener('visibilitychange', onVis); };
  }, [doRefresh]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, apiFetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
